'use client'
import { AppBar, Box, Stack, Toolbar } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { usePathname } from 'next/navigation'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import Brand from '@/components/common/header/components/Brand'
import SkipMenuButton from '@/components/common/header/components/SkipMenuButton'
import LocaleContext from '@/providers/context/LocaleContext'
import type { SiteProps } from '@/types/components'
import Link from '@/components/common/Link'

const MenuItem = styled(Link)(({ theme }) => ({
  fontFamily: `${theme.fontFamily.primary} !important`,
  fontWeight: '800',
  fontStretch: 'condensed',
  textDecoration: 'none !important',
  fontSize: '1.125rem',
  padding: 0,
  minWidth: 'unset',
  letterSpacing: 'inherit',
  alignSelf: 'center',
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.375rem'
  },
  color: `${theme.palette.text.primary} !important`,
  '&:hover, &:focus, &:focus-visible': {
    backgroundColor: 'transparent !important',
    color: theme.palette.primary.main + ' !important',
    textDecoration: 'underline !important'
  },
  '&.active': {
    color: theme.palette.primary.main + ' !important',
    textDecoration: 'underline !important'
  },
  [theme.accessibility.highContrast.mediaQuery('active')]: {
    color: 'CanvasText !important'
  }
}))

const BottomNavLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  flex: 1,
  textDecoration: 'none !important',
  color: `${theme.palette.text.primary} !important`,
  fontFamily: `${theme.fontFamily.primary} !important`,
  fontWeight: '700',
  fontStretch: 'condensed',
  fontSize: '0.7rem',
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  padding: '10px 0 4px',

  '&.active': {
    borderTop: '6px solid ' + theme.palette.primary.main,
    paddingTop: '4px',
    textDecoration: 'underline !important'
  },
  '& img': {
    filter: 'invert(1)'
  },

  [theme.accessibility.highContrast.mediaQuery('active')]: {
    color: 'CanvasText !important',
    '& .img-wrap': {
      forcedColorAdjust: 'none',
      background: theme.palette.background.default + ' !important',
      border: `1px solid ${theme.palette.divider} !important`
    }
  }
}))

export default function Site({ menu: { desktop } }: SiteProps) {
  const theme = useTheme()
  const pathname = usePathname()
  const { localePrefix } = useContext(LocaleContext)
  const { t } = useTranslation('title')

  const normalize = (str: string) => (str === '/' ? '/' : str.replace(/\/+$/, ''))

  return (
    <>
      <AppBar
        color='secondary'
        elevation={0}
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
          py: { xs: 0 },
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`
        }}>
        <SkipMenuButton />
        <Toolbar disableGutters sx={{ px: { xs: 2, md: 2.25, lg: 8 }, alignItems: 'center' }}>
          <Brand />
          <Stack
            direction={'row'}
            spacing={1}
            alignItems={'center'}
            sx={{
              ml: {
                xs: 4,
                lg: 12
              },
              gap: 4
            }}>
            {desktop?.map((item, i) => {
              return (
                <MenuItem key={i} href={item?.url} title={t(item?.title ?? '')} target={item?.target} sx={{ display: { xs: 'none', md: 'inline' } }}>
                  {t(item?.title ?? '')}
                </MenuItem>
              )
            })}
          </Stack>
        </Toolbar>
      </AppBar>

      <Box
        component='nav'
        aria-label={t('Mobile navigation')}
        sx={{
          position: 'fixed',
          bottom: '0',
          left: 0,
          right: 0,
          paddingBottom: '2px',
          zIndex: theme.zIndex.appBar,
          backgroundColor: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: { xs: 'flex', md: 'none' },
          justifyContent: 'space-around'
        }}></Box>
    </>
  )
}
